import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useSEO } from '@/hooks/useSEO';
import { useTherapyModalities, getTherapiesByStage, getEvidenceLevels, getStreamTypeOptions } from '@/hooks/useTherapyModalities';
import { useTherapyModalityStats, TherapyModalityStats } from '@/hooks/useTherapyModalityStats';
import { useTherapyEventCounts } from '@/hooks/useTherapyEventCounts';
import { useBotProtection } from '@/hooks/useBotProtection';
import { useWikiAccessBeacon } from '@/hooks/useWikiAccessBeacon';
import { useTeenMode, filterForTeenMode } from '@/hooks/useTeenMode';
import TeenModeBanner from '@/components/therapy/TeenModeBanner';

import TherapyFilters from '@/components/therapy/TherapyFilters';
import ArchiveGrid from '@/components/therapy/ArchiveGrid';
import DeepModePanel from '@/components/framework/DeepModePanel';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const TherapyWikipedia: React.FC = () => {
  const { t, i18n } = useTranslation('therapyLibrary');
  const isZh = i18n.language === 'zh';
  const { isBot, isReady } = useBotProtection();
  const { teenMode } = useTeenMode();

  const { data: rawTherapies, isLoading, error, refetch } = useTherapyModalities();
  const { data: stats } = useTherapyModalityStats();
  const { data: eventCountsMap } = useTherapyEventCounts();

  const therapies = useMemo(
    () => filterForTeenMode(rawTherapies, teenMode),
    [rawTherapies, teenMode]
  );

  // Provenance beacon: coarse trace of which entries this reader was served.
  const servedIds = useMemo(() => (therapies ?? []).map((x) => x.id), [therapies]);

  useWikiAccessBeacon('therapy_wiki', servedIds);

  const statsMap = useMemo(() => {
    if (!stats) return new Map<string, TherapyModalityStats>();
    return new Map(stats.map((s) => [s.therapy_modality_id, s]));
  }, [stats]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStage, setSelectedStage] = useState('all');
  const [selectedEvidenceLevel, setSelectedEvidenceLevel] = useState('all');
  const [selectedStreamType, setSelectedStreamType] = useState('all');

  // Note: the standalone "Discover your healing stage" survey has been
  // retired here. Stage discovery now happens conversationally through the
  // homepage Reso chat (WellnessConcierge), which can hand off into Deep
  // Mode on this page when the user wants to go further.


  useSEO({
    title: t('seoTitle', { defaultValue: t('pageTitle') }),
    description: t('pageSubtitle'),
  });

  const evidenceLevels = useMemo(() => (therapies ? getEvidenceLevels(therapies) : []), [therapies]);
  const streamTypes = useMemo(() => (therapies ? getStreamTypeOptions(therapies) : []), [therapies]);

  const filteredTherapies = useMemo(() => {
    if (!therapies) return [];
    return therapies.filter((therapy) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = therapy.name_en.toLowerCase().includes(query) ||
                           therapy.name_cn.toLowerCase().includes(query) ||
                           (therapy.abbreviation?.toLowerCase().includes(query));
        const matchesMechanism = (therapy.mechanism_en?.toLowerCase().includes(query)) ||
                                 (therapy.mechanism_cn?.toLowerCase().includes(query));
        const matchesCert = (therapy.certifications || []).some((c) => c?.toLowerCase().includes(query));
        const matchesNarrative = [
          therapy.origin_narrative,
          therapy.origin_narrative_en,
          therapy.population_prose,
          therapy.population_prose_en,
          therapy.stream_type_cn,
          therapy.type_cn,
        ].some((v) => v?.toLowerCase().includes(query));
        if (!matchesName && !matchesMechanism && !matchesCert && !matchesNarrative) return false;
      }
      if (selectedStage !== 'all') {
        const stageKey = `stage_${selectedStage}` as keyof typeof therapy;
        if (!therapy[stageKey]) return false;
      }
      if (selectedEvidenceLevel !== 'all' && therapy.evidence_level !== selectedEvidenceLevel) return false;
      if (selectedStreamType !== 'all' && therapy.stream_type !== selectedStreamType) return false;
      return true;
    });
  }, [therapies, searchQuery, selectedStage, selectedEvidenceLevel, selectedStreamType]);

  const therapiesByStage = useMemo(() => {
    const stages: { [key: number]: typeof filteredTherapies } = {};
    for (let i = 0; i <= 4; i++) {
      stages[i] = getTherapiesByStage(filteredTherapies, i);
    }
    return stages;
  }, [filteredTherapies]);

  const hasActiveFilters = searchQuery !== '' ||
                          selectedStage !== 'all' ||
                          selectedEvidenceLevel !== 'all' ||
                          selectedStreamType !== 'all';

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStage('all');
    setSelectedEvidenceLevel('all');
    setSelectedStreamType('all');
  };

  const visibleStages = [0, 1, 2, 3, 4].filter((n) => (therapiesByStage[n]?.length || 0) > 0);

  if (!isReady) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  if (isBot) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-serif text-earth-900 mb-4">Content Protected</h1>
          <p className="text-earth-700">This content is protected from automated access.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{t('error')}</p>
          <Button onClick={() => refetch()} variant="default">{t('retry')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50/50">
      <Navbar />

      <main className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Deep Mode — AI continuity from homepage Reso */}
          <DeepModePanel />

          {teenMode && <TeenModeBanner />}


          {/* Editorial title block */}
          <header className="text-center mb-16 md:mb-20">
            <p className="text-[10px] uppercase tracking-[0.35em] text-stone-500 font-sans mb-5">
              {t('archiveLabel', { defaultValue: 'Healing Knowledge Archive' })}
            </p>
            <h1 className="font-serif italic text-4xl md:text-6xl text-stone-900 leading-[1.05] tracking-tight mb-6">
              {t('pageTitle')}
            </h1>
            <p className="max-w-2xl mx-auto text-base md:text-lg text-stone-600 leading-relaxed font-sans">
              {t('pageSubtitle')}
            </p>
            <p className="max-w-xl mx-auto mt-5 text-xs md:text-sm text-stone-500 leading-relaxed font-sans">
              {t('readingNote')}
            </p>
          </header>

          {/* Filters */}
          <div className="mb-10">
            <TherapyFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedStage={selectedStage}
              onStageChange={setSelectedStage}
              selectedEvidenceLevel={selectedEvidenceLevel}
              onEvidenceLevelChange={setSelectedEvidenceLevel}
              selectedStreamType={selectedStreamType}
              onStreamTypeChange={setSelectedStreamType}
              evidenceLevels={evidenceLevels}
              streamTypes={streamTypes}
              onClear={clearFilters}
              hasActiveFilters={hasActiveFilters}
            />
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-stone-500 font-sans">
              {t('therapiesCount', { count: filteredTherapies.length })}
            </p>
          </div>

          {/* Archive Grid — chapters */}
          {visibleStages.length > 0 ? (
            <ArchiveGrid
              therapiesByStage={therapiesByStage}
              visibleStages={visibleStages}
              statsMap={statsMap}
              eventCountsMap={eventCountsMap}
            />
          ) : (
            <p className="text-center text-stone-500 py-16 font-serif italic text-lg">
              {t('noResults')}
            </p>
          )}

          {/* Standalone "Healing Stage Survey" removed.
              Stage discovery is now conversational via the homepage Reso
              chat; Deep Mode is triggered from that conversation. */}


          {/* Soft CTA */}
          <div className="mt-16 text-center">
            <Link
              to="/booking-choice"
              className="inline-flex items-center text-[11px] font-semibold tracking-[0.2em] uppercase text-stone-900 hover:text-[hsl(var(--primary))] transition-colors font-sans"
            >
              {t('cta.button')} →
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TherapyWikipedia;
